import * as d3 from "d3";
import * as con from "../Constants"
import { formatEValue, buildArrow, extractIdFromTaxonomicSequence, getTaxomomicId, trimSequenceId } from "../utils/chartFunctions";
import { sleep } from "../utils/generalFunctions";
import { setStoredValue } from "../store/actions";

// Chart constants
export const CHART_WIDTH = 900
export const CHART_HEIGHT = 290
export const CHART_MARGIN = 10

export const POSITION_LABEL_HEIGHT = 20
export const SEQUENCE_LABEL_WIDTH = 100
export const SEQUENCE_SCORE_WIDTH = 45
export const SEQUENCE_SIMILARITY_WIDTH = 45

export const MATCHES_NUMBER_OF_TICKS = 10




export class AlignmentResultsChart {


    // Lists of attributes
    initialized = false // If the chart has been initialized or not


    querySequenceLength = 1000 // Query sequence length. Starts on 100 for visualization
    plottedQuerySequenceLength = this.querySequenceLength // Plotted query sequence lenght, used for avoided rerenderings


    constructor(parameters, objectReference) {

        // Binds
        this.objectReference = objectReference

        // Assigns parameters
        this.parameters = { ...parameters }

    }

    /**
     * Method that initializes the chart. It sets the background structure so that it looks
     * rewady to load but empty
     */
    initialize() {

        // Binds again
        //let self = this;

        this.svgEl = d3.select(this.objectReference.current)

        // Removes all inside elements
        this.svgEl.selectAll("*").remove(); // Clear svg content before adding new elements 

        // Adds the tooltip (remove existing one first to avoid duplicates)
        d3.select("#tooltip").remove();
        this.tooltip = d3.select("body")
            .append("div")
            .attr("id", "tooltip")
            .style("position", "absolute")
            .style("pointer-events", "none")
            .style("background", "#eee")
            .style("border", "1px solid #ccc")
            .style("padding", "5px")
            .style("font-size", "12px")
            .style("display", "none");

        // Construct view box
        this.svg = this.svgEl.attr("viewBox", `0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`)
            .attr('width', '100%')



        // Add the margin
        this.svg.append("rect")
            .attr("x", CHART_MARGIN)
            .attr("y", CHART_MARGIN)
            .attr("height", CHART_HEIGHT - 2 * CHART_MARGIN)
            .attr("width", CHART_WIDTH - 2 * CHART_MARGIN)
            .style("stroke", "var(--primary-color-1)")
            .style("fill", "#FFFFFF")
            .style("stroke-width", 1);


        // Starts the rows
        this.currentRows = 20
        this.querySequenceResultHeight = 20
        this.querySequenceResultMargin = 1


        this.querySequenceStartY = CHART_MARGIN + POSITION_LABEL_HEIGHT
        this.rowsStartY = this.querySequenceStartY + this.querySequenceResultHeight + this.querySequenceResultMargin
        this.rowsTotalHeight = CHART_HEIGHT - this.rowsStartY - CHART_MARGIN

        this.rowMaxWidth = CHART_WIDTH - 2 * CHART_MARGIN - SEQUENCE_LABEL_WIDTH - SEQUENCE_SCORE_WIDTH - SEQUENCE_SIMILARITY_WIDTH
        this.rowMargin = 0.05 * this.rowsTotalHeight / (this.currentRows)
        this.rowMaxheight = 0.95 * this.rowsTotalHeight / (this.currentRows)


        // Adds the components inside the chart

        // ---------------
        // Query Sequence        
        // ----------------

        // Name
        // ----------------
        this.querySequenceNamesBox = this.svg.append("g")
            .attr("transform", `translate(${CHART_MARGIN}, ${this.querySequenceStartY})`);


        this.querySequenceName = this.querySequenceNamesBox
            .append("g")


        this.querySequenceName.append("rect")
            .attr("width", SEQUENCE_LABEL_WIDTH)
            .attr("height", this.querySequenceResultHeight)
            .style("fill", "#AAAAAA")
            .style("opacity", 0.4);


        // Match
        // -----------------
        this.querySequenceMatcheBox = this.svg.append("g")
            .attr("transform", `translate(${CHART_MARGIN + SEQUENCE_LABEL_WIDTH}, ${this.querySequenceStartY})`);



        this.querySequenceMatches = this.querySequenceMatcheBox
            .append("g")


        this.querySequenceMatches.append("rect")
            .attr("width", this.rowMaxWidth)
            .attr("height", this.querySequenceResultHeight)
            .style("fill", "#AAAAAA")
            .style("opacity", 0.6);



        // Similarity
        // -----------------
        this.querySequencesSimilarityBox = this.svg.append("g")
            .attr("transform", `translate(${CHART_WIDTH - CHART_MARGIN - SEQUENCE_SCORE_WIDTH - SEQUENCE_SIMILARITY_WIDTH}, ${this.querySequenceStartY})`);

        // Add Similarity header above the similarity column
        this.svg.append("text")
            .attr("class", "similarity-header")
            .text(this.parameters?.i18n?.t ? this.parameters.i18n.t("identity") : "Identity")
            .attr("x", CHART_WIDTH - CHART_MARGIN - SEQUENCE_SCORE_WIDTH - SEQUENCE_SIMILARITY_WIDTH + (SEQUENCE_SIMILARITY_WIDTH / 2))
            .attr("y", CHART_MARGIN + (POSITION_LABEL_HEIGHT / 2))
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "central")
            .attr("fill", "black")
            .style("font-size", "10px")
            .style("font-weight", "bold");

        this.querySequenceSimilarity = this.querySequencesSimilarityBox
            .append("g")

        this.querySequenceSimilarity.append("rect")
            .attr("width", SEQUENCE_SIMILARITY_WIDTH)
            .attr("height", this.querySequenceResultHeight)
            .style("fill", "#AAAAAA")
            .style("opacity", 0.4);

        // Scores
        // -----------------
        this.querySequencesScoresBox = this.svg.append("g")
            .attr("transform", `translate(${CHART_WIDTH - CHART_MARGIN - SEQUENCE_SCORE_WIDTH}, ${this.querySequenceStartY})`);

        // Add E-value header above the scores column
        this.svg.append("text")
            .attr("class", "evalue-header")
            .text(this.parameters?.i18n?.t ? this.parameters.i18n.t("eValue") : "E-value")
            .attr("x", CHART_WIDTH - CHART_MARGIN - SEQUENCE_SCORE_WIDTH + (SEQUENCE_SCORE_WIDTH / 2))
            .attr("y", CHART_MARGIN + (POSITION_LABEL_HEIGHT / 2))
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "central")
            .attr("fill", "black")
            .style("font-size", "10px")
            .style("font-weight", "bold");

        this.querySequenceScore = this.querySequencesScoresBox
            .append("g")


        this.querySequenceScore.append("rect")
            .attr("width", SEQUENCE_SCORE_WIDTH)
            .attr("height", this.querySequenceResultHeight)
            .style("fill", "#AAAAAA")
            .style("opacity", 0.4);




        // --------
        // Results
        // --------

        // Sequence names
        // -----------------
        this.sequencesNamesBox = this.svg.append("g")
            .attr("transform", `translate(${CHART_MARGIN}, ${this.rowsStartY})`);


        this.sequenceNames = this.sequencesNamesBox.selectAll(".sequenceName")
            .data(new Array(this.currentRows))
            .enter().append("g")
            .attr("class", "sequenceName")
            .attr("transform", (d, i) => `translate(0, ${i * this.rowMaxheight + i * this.rowMargin})`);


        this.sequenceNames.append("rect")
            .attr("width", SEQUENCE_LABEL_WIDTH)
            .attr("height", this.rowMaxheight)
            .style("fill", "#AAAAAA")
            .style("opacity", (_, i) => i % 2 === 0 ? 0.1 : 0.3);


        // Sequence matches
        // -----------------
        this.sequenceMatchesBox = this.svg.append("g")
            .attr("transform", `translate(${CHART_MARGIN + SEQUENCE_LABEL_WIDTH}, ${this.rowsStartY})`);



        this.sequenceMatches = this.sequenceMatchesBox.selectAll(".sequenceMatch")
            .data(new Array(this.currentRows))
            .enter().append("g")
            .attr("class", "sequenceMatch")
            .attr("transform", (d, i) => `translate(0, ${i * this.rowMaxheight + i * this.rowMargin})`);



        this.sequenceMatches.append("rect")
            .attr("width", this.rowMaxWidth)
            .attr("height", this.rowMaxheight)
            .style("fill", "#AAAAAA")
            .style("opacity", (_, i) => i % 2 === 0 ? 0.2 : 0.5);



        // Sequence similarity
        // -----------------
        this.sequencesSimilarityBox = this.svg.append("g")
            .attr("transform", `translate(${CHART_WIDTH - CHART_MARGIN - SEQUENCE_SCORE_WIDTH - SEQUENCE_SIMILARITY_WIDTH}, ${this.rowsStartY})`);

        this.sequenceSimilarity = this.sequencesSimilarityBox.selectAll(".sequenceSimilarity")
            .data(new Array(this.currentRows))
            .enter().append("g")
            .attr("class", "sequenceSimilarity")
            .attr("transform", (d, i) => `translate(0, ${i * this.rowMaxheight + i * this.rowMargin})`);

        this.sequenceSimilarity.append("rect")
            .attr("width", SEQUENCE_SIMILARITY_WIDTH)
            .attr("height", this.rowMaxheight)
            .style("fill", "#AAAAAA")
            .style("opacity", (_, i) => i % 2 === 0 ? 0.1 : 0.3);

        // Sequence scores
        // -----------------
        this.sequencesScoresBox = this.svg.append("g")
            .attr("transform", `translate(${CHART_WIDTH - CHART_MARGIN - SEQUENCE_SCORE_WIDTH}, ${this.rowsStartY})`);


        this.sequenceScores = this.sequencesScoresBox.selectAll(".sequenceScore")
            .data(new Array(this.currentRows))
            .enter().append("g")
            .attr("class", "sequenceScore")
            .attr("transform", (d, i) => `translate(0, ${i * this.rowMaxheight + i * this.rowMargin})`);


        this.sequenceScores.append("rect")
            .attr("width", SEQUENCE_SCORE_WIDTH)
            .attr("height", this.rowMaxheight)
            .style("fill", "#AAAAAA")
            .style("opacity", (_, i) => i % 2 === 0 ? 0.1 : 0.3);


        // Match Labels
        // ------------------

        // Scale
        this.matchesScale = d3.scaleLinear()
            .domain([0, this.querySequenceLength]) // input values
            .range([0, this.rowMaxWidth]); // output pixel range

        // Axis
        this.xAxis = d3.axisTop(this.matchesScale)
            .ticks(MATCHES_NUMBER_OF_TICKS); // Adjust number of ticks as needed

        this.querySequenceMatches.append("g")
            .attr("class", "x-axis")
            .call(this.xAxis);

        const breaks = this.matchesScale.ticks(MATCHES_NUMBER_OF_TICKS);

        this.querySequenceMatches.selectAll(".break-line")
            .data(breaks)
            .enter()
            .append("line")
            .attr("class", "break-line")
            .attr("x1", d => this.matchesScale(d))
            .attr("x2", d => this.matchesScale(d))
            .attr("y1", 0)
            .attr("y2", this.querySequenceResultHeight + this.rowsTotalHeight)
            .attr("stroke", "black")
            .attr("stroke-width", 0.5);


        this.initialized = true





    }


    /**
     * Method that updated the chart to the current data. 
     * It removes everything and replaces it with the new data
     *  
     * @param {object} newData 
     */
    async update(newData) {

        this.clearAll()

        // User experience
        await sleep(1000)

        const thisClass = this

        // Sets the data
        this.data = newData
        this.querySequenceLength = this.data[con.ARC_QUERY_LENGTH]

        this.hits = this.data[con.ARC_HITS]
        this.hit_count = this.data[con.ARC_HITS].length

        // Updates the top axis
        this.updateAxisAndBreaks()

        this.clearAll()

        // Generates the color Palette
        let colorDomain = []
        if (this.hit_count > 0)
            colorDomain = this.hits.map(hit => getTaxomomicId(hit[con.ARC_SSQID]))

        // removes duplicates
        colorDomain = [...new Set(colorDomain)]
        // Max 20 hits
        const palette20 = d3.schemeTableau10.concat(d3.schemeSet3.slice(0, 10));

        this.colorScale = d3.scaleOrdinal()
            .domain(colorDomain) // or any other domain you want
            .range(palette20);


        // ---------------
        // Query Sequence        
        // ----------------

        // Name
        // ----------------
        this.querySequenceName.append("text")
            .text(trimSequenceId(this.data[con.ARC_QUERY_ID]))
            .attr("x", 3)      // X position within the <g>
            .attr("y", 3)      // Y position within the <g>
            .attr("dy", "1em") // Adjust vertical alignment
            .attr("fill", "black")
            .style("font-size", "10px");

        // Matches
        const startY = this.querySequenceResultHeight * 0.2;
        const endY = this.querySequenceResultHeight * 0.8;
        const startX = this.matchesScale(0);
        const endX = this.matchesScale(this.querySequenceLength);

        // Define the path points
        const pathData = buildArrow(startX, endX, startY, endY)

        // Append the path
        this.querySequenceMatches.append("path")
            .attr("class", "query-sequence-arrow")
            .attr("d", pathData)
            .attr("fill", "#2c5697")
            .attr("opacity", 0.9)
            .on("mouseover", (event) => {
                // Highlight the query sequence arrow
                d3.select(event.target).attr("opacity", 1.0);
                
                // Show tooltip
                thisClass.tooltip
                    .style("display", "block")
                    .html(thisClass.getQueryTooltipHTML());
                    
                // Get tooltip dimensions after content is set
                const tooltipNode = thisClass.tooltip.node();
                const tooltipWidth = tooltipNode.offsetWidth;
                const tooltipHeight = tooltipNode.offsetHeight;
                
                // Get viewport dimensions
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                
                // Calculate positions
                let left = event.pageX + 10;
                let top = event.pageY + 10;
                
                // Check if tooltip would go beyond right edge
                if (left + tooltipWidth > viewportWidth) {
                    left = event.pageX - tooltipWidth - 10;
                }
                
                // Check if tooltip would go beyond bottom edge
                if (top + tooltipHeight > viewportHeight) {
                    top = event.pageY - tooltipHeight - 10;
                }
                
                // Apply the calculated position
                thisClass.tooltip
                    .style("left", left + "px")
                    .style("top", top + "px");
            })
            .on("mouseout", (event) => {
                // Remove highlight
                d3.select(event.target).attr("opacity", 0.9);
                
                // Hide tooltip
                thisClass.tooltip.style("display", "none");
            });

        // Score

        // --------
        // Results
        // --------

        // Sequence names
        // -----------------
        this.sequenceNames.append("text")
            .attr("class", "result-name")
            .text((_, i) => i >= this.hit_count ? "" : extractIdFromTaxonomicSequence(this.hits[i][con.ARC_SSQID]))
            .attr("x", 1)      // X position within the <g>
            .attr("y", 0.5)      // Y position within the <g>
            .attr("dy", "1em") // Adjust vertical alignment
            .attr("fill", "black")
            .style("font-size", "8px");



        // Sequence matches
        // -----------------
        this.sequenceMatches.append("path")
            .attr("class", "result-sequence-arrow")
            .attr("d", (_, i) => i >= this.hit_count ? "" : buildArrow(this.matchesScale(this.hits[i][con.ARC_QSTART]),
                this.matchesScale(this.hits[i][con.ARC_QEND]),
                this.rowMaxheight * 0.2,
                this.rowMaxheight * 0.8,
                4,
                this.hits[i][con.ARC_SSTRAND] === con.ARC_PLUS ? con.RIGHT : con.LEFT
            ))
            .attr("fill", (_, i) => i >= this.hit_count ? null : this.colorScale(getTaxomomicId(this.hits[i][con.ARC_SSQID])))
            .attr("opacity", 0.5)
            .each(function (d, i) {

                if (i > thisClass.hit_count)
                    return


                d3.select(this)
                    .on("click", function (event, d) {

                        const ob = d3.select(this)

                        if (ob.classed("selected-sequence-arrow")) { // Checks if it selected
                            thisClass.resetSelection()
                            setStoredValue(con.SELECTED_ALIGNMENT_RESULT_HIT_ID, null)
                        }
                        else {
                            thisClass.resetSelection()
                            thisClass.setSelection(i)
                            setStoredValue(con.SELECTED_ALIGNMENT_RESULT_HIT_ID, thisClass.hits[i][con.ARC_SSQID])
                        }

                    })
                    .on("mouseover", function (event, d) {

                        const ob = d3.select(this)
                        if (!ob.classed("selected-sequence-arrow")) { // Checks if it selected
                            ob.attr("opacity", 0.85); // highlight on mouseout
                        }

                        // Tooltip
                        thisClass.tooltip
                            .style("display", "block")
                            .html(thisClass.getTooltipHTML(thisClass.hits[i]));
                            
                        // Get tooltip dimensions after content is set
                        const tooltipNode = thisClass.tooltip.node();
                        const tooltipWidth = tooltipNode.offsetWidth;
                        const tooltipHeight = tooltipNode.offsetHeight;
                        
                        // Get viewport dimensions
                        const viewportWidth = window.innerWidth;
                        const viewportHeight = window.innerHeight;
                        
                        // Calculate positions
                        let left = event.pageX + 10;
                        let top = event.pageY + 10;
                        
                        // Check if tooltip would go beyond right edge
                        if (left + tooltipWidth > viewportWidth) {
                            left = event.pageX - tooltipWidth - 10;
                        }
                        
                        // Check if tooltip would go beyond bottom edge
                        if (top + tooltipHeight > viewportHeight) {
                            top = event.pageY - tooltipHeight - 10;
                        }
                        
                        // Apply the calculated position
                        thisClass.tooltip
                            .style("left", left + "px")
                            .style("top", top + "px");

                    })
                    .on("mouseout", function (event, d) {

                        const ob = d3.select(this)
                        if (!ob.classed("selected-sequence-arrow")) { // Checks if it selected
                            ob.attr("opacity", 0.5); // dehighlight on mouseout
                        }

                        // Tooltip
                        thisClass.tooltip.style("display", "none");

                    })
            });




        // Sequence similarity
        // -----------------
        this.sequenceSimilarity.append("text")
            .attr("class", "result-similarity")
            .text((_, i) => i >= this.hit_count ? "" : `${parseFloat(this.hits[i][con.ARC_PIDENT]).toFixed(1)}%`)
            .attr("x", 1)      // X position within the <g>
            .attr("y", 0.5)      // Y position within the <g>
            .attr("dy", "1em") // Adjust vertical alignment
            .attr("fill", "black")
            .style("font-size", "8px");

        // Sequence scores
        // -----------------
        this.sequenceScores.append("text")
            .attr("class", "result-name")
            .text((_, i) => i >= this.hit_count ? "" : formatEValue(this.hits[i][con.ARC_EVALUE]))
            .attr("x", 1)      // X position within the <g>
            .attr("y", 0.5)      // Y position within the <g>
            .attr("dy", "1em") // Adjust vertical alignment
            .attr("fill", "black")
            .style("font-size", "8px");


    }


    // -------------------
    // Support Methods
    // -------------------

    clearAll() {

        // Query Sequence
        this.querySequenceName.selectAll("text").remove();
        this.querySequenceMatches.selectAll("path").remove();

        // Results
        this.sequenceNames.selectAll("text").remove();
        this.sequenceMatches.selectAll("path").remove();
        this.sequenceSimilarity.selectAll("text").remove();
        this.sequenceScores.selectAll("text").remove();

    }

    resetSelection() {

        // Arrows
        this.sequenceMatches
            .selectAll("path")
            .classed("selected-sequence-arrow", false)
            .attr("opacity", 0.5);

        // Names
        this.sequenceNames
            .selectAll("text")
            .classed("selected-sequence-name", false)

    }

    setSelection(selectedIndex) {

        // Arrow
        this.sequenceMatches
            .select("path")   // select the child <text> in each <g>
            .filter((_, i) => i === selectedIndex)
            .classed("selected-sequence-arrow", true); // add your custom class

        // Names
        this.sequenceNames
            .select("text")
            .filter((_, i) => i === selectedIndex)
            .classed("selected-sequence-name", true)

    }

    /**
     * Support function that updates the top axis of the chart.
     * It will only run if a new QuerySequenceLenght is detected     
     */
    updateAxisAndBreaks() {

        // No need to rerender
        if (this.querySequenceLength === this.plottedQuerySequenceLength)
            return

        // Rounded value
        const roundedQuerySequenceLength = 10 * Math.ceil(this.querySequenceLength / 10)

        // 1. Update scale domain
        this.matchesScale.domain([0, roundedQuerySequenceLength]);

        // 2. Update axis
        this.xAxis = d3.axisTop(this.matchesScale)
            .ticks(MATCHES_NUMBER_OF_TICKS);

        this.querySequenceMatches.select(".x-axis")
            .transition()
            .duration(500)
            .call(this.xAxis);

        // 3. Compute new breaks
        const breaks = this.matchesScale.ticks(MATCHES_NUMBER_OF_TICKS);


        // 4. Join + update vertical lines
        const lines = this.querySequenceMatches.selectAll(".break-line")
            .data(breaks, d => d);

        // 4a. Remove old lines
        lines.exit().remove();

        // 4b. Update existing lines
        lines.transition()
            .duration(500)
            .attr("x1", d => this.matchesScale(d))
            .attr("x2", d => this.matchesScale(d));

        // 4c. Add new lines
        lines.enter()
            .append("line")
            .attr("class", "break-line")
            .attr("y1", 0)
            .attr("y2", this.querySequenceResultHeight + this.rowsTotalHeight)
            .attr("stroke", "black")
            .attr("stroke-width", 0.5)
            .attr("x1", d => this.matchesScale(d))
            .attr("x2", d => this.matchesScale(d));

        // Set the plotted query sequence length
        this.plottedQuerySequenceLength = this.querySequenceLength

    }

    // Per-hit tooltip for each bar in the alignment results
    getTooltipHTML(hitData) {
        let taxonomyHTML = '';
        
        if (hitData[con.ARC_TAXONOMY]) {
            taxonomyHTML = '<hr>';
            con.TAXONOMIC_LEVELS.forEach(level => {
                if (hitData[con.ARC_TAXONOMY][level]) {
                    taxonomyHTML += `<strong>${level.charAt(0).toUpperCase() + level.slice(1)}:</strong> ${hitData[con.ARC_TAXONOMY][level]}<br>`;
                }
            });
        }
        
        // Format similarity and coverage to 2 decimal places
        const similarity = parseFloat(hitData[con.ARC_PIDENT]).toFixed(2);
        const coverage = parseFloat(hitData[con.ARC_QCOVS]).toFixed(2);
        
        // Format e-value
        const evalue = formatEValue(hitData[con.ARC_EVALUE]);

        return `
        <div>
            <strong>Similarity:</strong> ${similarity}%<br>
            <strong>Coverage:</strong> ${coverage}%<br>
            <strong>E-value:</strong> ${evalue}<br>
            ${taxonomyHTML}
        </div>
    `;
    }

    /**
     * Calculate GC content percentage from a DNA sequence
     * @param {string} sequence - DNA sequence string
     * @returns {number} GC content as percentage (0-100)
     */
    calculateGCContent(sequence) {
        if (!sequence || typeof sequence !== 'string') {
            return 0;
        }
        
        // Convert to uppercase and remove non-nucleotide characters
        const cleanSequence = sequence.toUpperCase().replace(/[^ATCG]/g, '');
        
        if (cleanSequence.length === 0) {
            return 0;
        }
        
        // Count G and C nucleotides
        const gcCount = (cleanSequence.match(/[GC]/g) || []).length;
        
        // Calculate percentage
        return ((gcCount / cleanSequence.length) * 100).toFixed(1);
    }

    getQueryTooltipHTML() {
        // Try to get query sequence from the hits' alignment data
        let querySequence = null;
        if (this.hits && this.hits.length > 0) {
            // Try to find a hit with alignment data
            const hitWithAlignment = this.hits.find(hit => 
                hit[con.ARC_ALIGNMENT] && hit[con.ARC_ALIGNMENT][con.ARC_QSEQ]
            );
            if (hitWithAlignment) {
                querySequence = hitWithAlignment[con.ARC_ALIGNMENT][con.ARC_QSEQ];
            }
        }
        
        let gcContent = "N/A";
        if (querySequence) {
            const gcValue = this.calculateGCContent(querySequence);
            gcContent = gcValue + "%";
        }
        
        // Safely get query ID and length - show full query ID in tooltip (no trimming)
        const queryId = this.data[con.ARC_QUERY_ID] || "Unknown";
        const queryLength = this.data[con.ARC_QUERY_LENGTH] || "Unknown";
        
        return `
        <div>
            <strong>Query ID:</strong> ${queryId}<br>
            <strong>Sequence length:</strong> ${queryLength}${typeof queryLength === 'number' ? ' bp' : ''}<br>
            <strong>GC content:</strong> ${gcContent}
        </div>
    `;
    }

}
